import React, { memo, useMemo } from 'react';

import { Box, ButtonBase, Link, Typography, useTheme } from '@/shared/ui';
import { AddToPhotosOutlined, ExpandMore, LockOutlined } from '@/shared/ui/icons';

import type { ReactNode } from 'react';

import type { CatalogItem } from '@/entities/catalog-item';
import type { SymbolKey } from '@/entities/symbol';
import { formatNumberWithPrecision } from '@/shared/lib/format/formatNumberWithPrecision';
import { getDiffColor } from '@/shared/lib/format/getDiffColor';
import Tooltip from '@/shared/ui/primitives/Tooltip';
import { ADDITIONAL_ITEM } from '../../../../constants';
import { TITLE_CHEVRON_GUTTER } from '../../constants';
import { SymbolCheckbox } from '../SymbolCheckbox/SymbolCheckbox';
import useCatalogTableCell from '../useCatalogTableCell';

import type { Cell } from '@tanstack/react-table';

const factsheetCellSx = { display: 'flex', justifyContent: 'center', width: '100%' };

const AdditionalItem = () => {
  return (
    <div style={{ width: '100%' }}>
      <Tooltip
        title={
          <>
            <Typography sx={{ fontSize: 14 }}>This GX code has additional items.</Typography>
            <Typography sx={{ fontSize: 14 }}>Check Display settings to view.</Typography>
          </>
        }
      >
        <span
          style={{
            display: 'inline-block',
            maxWidth: '100%'
          }}
        >
          <AddToPhotosOutlined sx={{ fontSize: '16px' }} />
        </span>
      </Tooltip>
    </div>
  );
};

const TitleExpandChevron = ({
  code,
  isExpanded,
  onToggleExpand
}: {
  code: string;
  isExpanded?: boolean;
  onToggleExpand?: (code: string) => void;
}) => (
  <ButtonBase
    aria-expanded={Boolean(isExpanded)}
    aria-label={isExpanded ? 'Collapse periods' : 'Expand periods'}
    disableRipple
    sx={{ flexShrink: 0, p: 0, color: 'inherit' }}
    onClick={event => {
      event.stopPropagation();
      onToggleExpand?.(code);
    }}
  >
    <ExpandMore
      sx={{
        fontSize: 16,
        transition: 'transform 150ms',
        transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'
      }}
    />
  </ButtonBase>
);

const SpotCatalogCell = ({
  cell,
  isRestrictedData,
  symbol,
  isInDashboard,
  renderFactsheetButton,
  canExpand,
  isExpanded,
  onToggleExpand
}: {
  cell: Cell<CatalogItem, unknown>;
  isRestrictedData: boolean;
  symbol?: SymbolKey;
  isInDashboard: boolean;
  renderFactsheetButton?: (code: string, mode: 'modal' | 'newTab') => ReactNode;
  canExpand?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: (code: string) => void;
}) => {
  const columnId = cell.column.id;
  const cellValue = cell.getValue() as string;

  const { palette } = useTheme();
  const { renderContent, defaultStyle } = useCatalogTableCell(columnId, cell);

  const restrictedTitle = useMemo(
    () => (
      <>
        You don't have access to this.{' '}
        <Link
          color='gx.text'
          href='https://www.general-index.com/contact-us'
          rel='noopener noreferrer'
          sx={{ fontWeight: 700 }}
          target='_blank'
        >
          Request access
        </Link>
      </>
    ),
    []
  );

  const returnCellColor = (isChangeColumn?: boolean, value?: string) => {
    if (isRestrictedData) return palette.table.infoRowText;
    if (isChangeColumn) return getDiffColor(Number(value));
    return 'inherit';
  };
  const spotStyle = {
    ...defaultStyle,
    justifyContent: ['Last', 'Change'].includes(columnId) ? 'flex-end' : 'flex-start',
    color: returnCellColor(columnId === 'Change', cellValue)
  };
  const renderCellContent = () => {
    switch (columnId) {
      case ADDITIONAL_ITEM: {
        if (!cell.row.original[ADDITIONAL_ITEM]) return <span />;
        return <AdditionalItem />;
      }
      case 'Symbol': {
        if (!symbol) return <span />;
        return (
          <SymbolCheckbox
            disabled={isRestrictedData}
            symbol={symbol}
          />
        );
      }
      case 'Index':
        return <span>{cellValue === 'Y' ? 'Index' : 'High, Mid, Low'}</span>;
      case 'Last':
      case 'Change':
        if (isRestrictedData) {
          return (
            <Tooltip title={restrictedTitle}>
              <LockOutlined
                color='inherit'
                sx={{ fontSize: '16px' }}
              />
            </Tooltip>
          );
        }
        return <span>{formatNumberWithPrecision(cellValue)}</span>;
      case 'Factsheet':
        return renderFactsheetButton ? (
          <Box sx={factsheetCellSx}>
            {renderFactsheetButton(cell.row.getValue('Code'), isInDashboard ? 'newTab' : 'modal')}
          </Box>
        ) : null;
      case 'Title': {
        const code = cell.row.getValue<string>('Code');
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
            {/* Fixed-width slot so the chevron never pushes into the title and every
                title starts at the same x whether or not the row is expandable. */}
            <Box
              sx={{
                width: `${TITLE_CHEVRON_GUTTER}px`,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {canExpand && (
                <TitleExpandChevron
                  code={code}
                  isExpanded={isExpanded}
                  onToggleExpand={onToggleExpand}
                />
              )}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>{renderContent()}</Box>
          </Box>
        );
      }
      default:
        return <>{renderContent()}</>;
    }
  };

  return (
    <div
      key={cell.id + columnId}
      style={spotStyle}
    >
      {renderCellContent()}
    </div>
  );
};

export default memo(SpotCatalogCell);
